/**
 * TaskSubmissionStore
 *
 * Screen-scoped domain store for submission authoring.
 * Holds observable UI/domain state and pure derivations only.
 * No navigation, dialogs, or network effects here.
 */

import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { Task, Attachment } from '@/student/types';
import { getFlatEvaluationDimensions } from '@/common/utils/evaluation/getFlatEvaluationDimensions';
import { allDimensionsMeetThresholds } from '../components/aiTutor/evaluation/levels';
// eslint-disable-next-line import/no-named-as-default
import countWords, { wordCountBounds } from '../../../common/utils/wordCount';
import getWordcountColor from '../composer/utils/getWordcountColor';
import type {
  AiTutorFormativeFeedback,
  AiTutorMessage,
  SubmissionEvaluationResult,
  TaskEvaluationConfigInputCompletionMinLevelsOverrideAnyOf,
} from '../../../common/api/models';

// Accessors

export type AutosaveStatus = 'idle' | 'inflight' | 'saved' | 'error';

export interface Wordcount {
  bodyWords: number;
  allWords: number;
}

export interface SubmissionSlice {
  html: string;
  taskId: string | null;
  wordcount: Wordcount;
  wordcountTargets: { min: number; target: number; max: number };
  dirty: boolean;
  lastSavedAt?: number;
  etag: string | null;
}

export interface AutosaveSlice {
  status: AutosaveStatus;
  errorMessage?: string;
  errorCode?: string;
}

export type EvaluationStreamProgress = {
  stage: 'stage1' | 'stage2';
  dimsDone: number;
  dimsTotal: number;
  elapsedMs: number;
};

/** The draft was saved after the evaluation it is showing, so that evaluation no longer describes it. */
export const isEditedSinceEvaluation = (lastSavedAt?: number, evaluatedAt?: number): boolean => Boolean(
  evaluatedAt && lastSavedAt && lastSavedAt > evaluatedAt,
);

/** Canonical extended-wait threshold for AI Tutor evaluation streams (server-reported elapsedMs). */
export const EXTENDED_WAIT_THRESHOLD_MS = 45_000;

export interface EvaluationSlice {
  data: SubmissionEvaluationResult | null;
  evaluatedAt?: number;
  isEvaluating: boolean;
  /** Canonical user-facing message when the evaluation stream fails or times out. */
  error: string | null;
  completionPolicy: 'free' | 'gated';
  minLevelsEffective: TaskEvaluationConfigInputCompletionMinLevelsOverrideAnyOf | 'satisfactory' | null;
  readiness: boolean | null;
  canSubmit: boolean;
  /** Rubric dimension names expected for the in-flight evaluation stream. */
  expectedDims: string[];
  /** Server-reported progress during streaming evaluation. */
  progress: EvaluationStreamProgress | null;
}

export interface AttachmentsSlice {
  items: Attachment[]; // Complex attachment type, can be typed later
  isUploading: boolean;
  lastError?: string;
}

export interface UiSlice {
  readonly: boolean;
  isOffline: boolean;
}

export interface ReactionSlice {
  isReacting: boolean;
  pendingReactionMessage: {
    id: string;
    content: string;
    createdAt: Date;
  } | null;
  error?: string;
}

export interface ChatSlice {
  messages: AiTutorMessage[];
}

export interface FormativeFeedbackSlice {
  data: AiTutorFormativeFeedback | null;
  /** In-flight partial text while formative feedback streams. */
  partial: Partial<AiTutorFormativeFeedback> | null;
  version: number;
  /** Evaluation run id bound to the current formative feedback. */
  runId: string | null;
}

export interface TaskSubmissionState {
  submission: SubmissionSlice;
  autosave: AutosaveSlice;
  evaluation: EvaluationSlice;
  attachments: AttachmentsSlice;
  ui: UiSlice;
  reaction: ReactionSlice;
  chat: ChatSlice;
  formativeFeedback: FormativeFeedbackSlice;

  // actions (pure)
  setContent: (html: string) => void;
  setWordcount: (count: Wordcount) => void;
  setContentAndWordcount: (html: string) => void;
  setDirty: (flag: boolean) => void;
  setAutosaveState: (status: AutosaveStatus, errorMessage?: string, errorCode?: string) => void;
  setLastSavedAt: (ts: number | undefined) => void;
  setEtag: (etag: string | null) => void;

  setEvaluation: (data: SubmissionEvaluationResult | null, evaluatedAt?: number) => void;
  setEvaluationDim: (name: string, level: string) => void;
  setExpectedDims: (dims: string[]) => void;
  setEvaluationProgress: (progress: EvaluationStreamProgress | null) => void;
  clearEvaluationStreamUi: () => void;
  clearEvaluationError: () => void;
  /** Record a stream failure and discard partial in-flight grading/feedback. */
  failEvaluation: (message: string) => void;
  /** Cancel an in-flight stream without recording an error. */
  abortEvaluation: () => void;
  /**
   * Clears all evaluation + formative-feedback domain state so the UI shows a fresh
   * "no evaluation yet" baseline. Used by the lecturer preview when switching tasks
   * (each preview task is a clean slate) and before starting a new preview stream.
   *
   * Note: formativeFeedback.version is kept monotonic on purpose. The
   * FormativeFeedbackCard uses lastSeenVersionRef to detect new feedback and
   * reset thumb-up/down controls; resetting version to 0 would re-trigger that
   * reset path on the next stream.
   */
  resetEvaluation: () => void;
  setEvaluating: (flag: boolean) => void;
  setCompletionPolicy: (policy: 'free' | 'gated') => void;
  setMinLevelsEffective: (levels: EvaluationSlice['minLevelsEffective']) => void;
  setReadiness: (ready: boolean | null) => void;
  recomputeCanSubmit: () => void;

  initializeFromTask: (task: Task) => void;

  setAttachments: (items: Attachment[]) => void;
  setUploading: (flag: boolean) => void;
  setAttachmentError: (message?: string) => void;

  setReadonly: (flag: boolean) => void;
  setOffline: (flag: boolean) => void;

  setReacting: (flag: boolean) => void;
  updatePendingReaction: (content: string) => void;
  clearPendingReaction: () => void;
  setReactionError: (message?: string) => void;

  setChatMessages: (messages: AiTutorMessage[]) => void;
  appendChatMessage: (message: AiTutorMessage) => void;
  updateChatMessage: (id: string, updates: Partial<AiTutorMessage>) => void;
  clearChatMessages: () => void;

  setFormativeFeedback: (data: AiTutorFormativeFeedback | null, runId?: string | null) => void;
  setFormativeFeedbackPartial: (partial: Partial<AiTutorFormativeFeedback> | null) => void;

  reset: (initial?: Partial<TaskSubmissionState>) => void;
}

const initialState: Omit<TaskSubmissionState, keyof TaskSubmissionState> & TaskSubmissionState = {
  submission: {
    html: '',
    taskId: null,
    wordcount: { bodyWords: 0, allWords: 0 },
    wordcountTargets: { min: 0, target: 0, max: 0 },
    dirty: false,
    lastSavedAt: undefined,
    etag: null,
  },
  autosave: { status: 'idle', errorMessage: undefined, errorCode: undefined },
  evaluation: {
    data: null,
    evaluatedAt: undefined,
    isEvaluating: false,
    error: null,
    completionPolicy: 'free',
    minLevelsEffective: null,
    readiness: null,
    canSubmit: true,
    expectedDims: [],
    progress: null,
  },
  attachments: { items: [], isUploading: false, lastError: undefined },
  ui: { readonly: false, isOffline: false },
  reaction: { isReacting: false, pendingReactionMessage: null, error: undefined },
  chat: { messages: [] },
  formativeFeedback: {
    data: null,
    partial: null,
    version: 0,
    runId: null,
  },

  setContent: () => {},
  setWordcount: () => {},
  setContentAndWordcount: () => {},
  setDirty: () => {},
  setAutosaveState: () => {},
  setLastSavedAt: () => {},
  setEtag: () => {},
  setEvaluation: () => {},
  setEvaluationDim: () => {},
  setExpectedDims: () => {},
  setEvaluationProgress: () => {},
  clearEvaluationStreamUi: () => {},
  clearEvaluationError: () => {},
  failEvaluation: () => {},
  abortEvaluation: () => {},
  resetEvaluation: () => {},
  setEvaluating: () => {},
  setCompletionPolicy: () => {},
  setMinLevelsEffective: () => {},
  setReadiness: () => {},
  recomputeCanSubmit: () => {},
  setAttachments: () => {},
  setUploading: () => {},
  setAttachmentError: () => {},
  setReadonly: () => {},
  setOffline: () => {},
  setReacting: () => {},
  updatePendingReaction: () => {},
  clearPendingReaction: () => {},
  setReactionError: () => {},
  setChatMessages: () => {},
  appendChatMessage: () => {},
  updateChatMessage: () => {},
  clearChatMessages: () => {},
  setFormativeFeedback: () => {},
  setFormativeFeedbackPartial: () => {},
  initializeFromTask: () => {},
  reset: () => {},
};

export const useTaskSubmissionStore = createWithEqualityFn<TaskSubmissionState>((set, get) => ({
  ...initialState,

  setContent: (html) => set((state) => ({ submission: { ...state.submission, html } })),
  setWordcount: (count) => set((state) => ({ submission: { ...state.submission, wordcount: count } })),
  setContentAndWordcount: (html) => set((state) => ({
    submission: {
      ...state.submission,
      html,
      wordcount: {
        bodyWords: countWords(html, true),
        allWords: countWords(html, false),
      },
      dirty: true,
    },
  })),
  setDirty: (flag) => set((state) => ({ submission: { ...state.submission, dirty: flag } })),
  setAutosaveState: (status, errorMessage, errorCode) => {
    set((state) => {
      const updates: Partial<TaskSubmissionState> = {
        autosave: { status, errorMessage, errorCode },
      };

      // If we just saved, update lastSavedAt and clear dirty flag simultaneously
      // to avoid race conditions where the UI might flicker or miss the state change
      if (status === 'saved') {
        updates.submission = {
          ...state.submission,
          lastSavedAt: Date.now(),
          dirty: false,
        };
      }

      return updates;
    });
    if (status === 'saved') get().recomputeCanSubmit();
  },
  setLastSavedAt: (ts) => {
    set((state) => ({ submission: { ...state.submission, lastSavedAt: ts } }));
    get().recomputeCanSubmit();
  },
  setEtag: (etag) => set((state) => ({ submission: { ...state.submission, etag } })),

  setEvaluation: (data, evaluatedAt) => {
    set((state) => ({ evaluation: { ...state.evaluation, data, evaluatedAt } }));
    get().recomputeCanSubmit();
  },
  setEvaluationDim: (name, level) => set((state) => ({
    evaluation: {
      ...state.evaluation,
      data: { ...(state.evaluation.data || {}), [name]: { level } },
    },
  })),
  setExpectedDims: (dims) => set((state) => ({ evaluation: { ...state.evaluation, expectedDims: dims } })),
  setEvaluationProgress: (progress) => set((state) => ({ evaluation: { ...state.evaluation, progress } })),
  clearEvaluationStreamUi: () => set((state) => ({
    evaluation: { ...state.evaluation, expectedDims: [], progress: null },
    formativeFeedback: { ...state.formativeFeedback, partial: null },
  })),
  clearEvaluationError: () => set((state) => ({
    evaluation: { ...state.evaluation, error: null },
  })),
  failEvaluation: (message) => set((state) => ({
    evaluation: {
      ...state.evaluation,
      isEvaluating: false,
      error: message,
      data: null,
      evaluatedAt: undefined,
      expectedDims: [],
      progress: null,
    },
    formativeFeedback: {
      ...state.formativeFeedback,
      data: null,
      partial: null,
      runId: null,
    },
  })),
  abortEvaluation: () => set((state) => ({
    evaluation: {
      ...state.evaluation,
      isEvaluating: false,
      error: null,
      data: null,
      evaluatedAt: undefined,
      expectedDims: [],
      progress: null,
    },
    formativeFeedback: {
      ...state.formativeFeedback,
      data: null,
      partial: null,
      runId: null,
    },
  })),
  resetEvaluation: () => set((state) => ({
    evaluation: {
      ...state.evaluation,
      data: null,
      evaluatedAt: undefined,
      isEvaluating: false,
      error: null,
      expectedDims: [],
      progress: null,
      readiness: null,
    },
    formativeFeedback: {
      ...state.formativeFeedback,
      data: null,
      partial: null,
      runId: null,
    },
  })),
  setEvaluating: (flag) => set((state) => ({ evaluation: { ...state.evaluation, isEvaluating: flag } })),
  setCompletionPolicy: (policy) => set((state) => ({ evaluation: { ...state.evaluation, completionPolicy: policy } })),
  setMinLevelsEffective: (levels) => set((state) => ({ evaluation: { ...state.evaluation, minLevelsEffective: levels } })),
  setReadiness: (ready) => set((state) => ({ evaluation: { ...state.evaluation, readiness: ready } })),
  recomputeCanSubmit: () => {
    const { evaluation, formativeFeedback, submission } = get();
    if (evaluation.completionPolicy !== 'gated') {
      set((state) => ({ evaluation: { ...state.evaluation, canSubmit: true } }));
      return;
    }
    if (isEditedSinceEvaluation(submission.lastSavedAt, evaluation.evaluatedAt)) {
      set((state) => ({ evaluation: { ...state.evaluation, canSubmit: false } }));
      return;
    }
    const thresholds = evaluation.minLevelsEffective;
    const hasThresholds = thresholds != null
      && (typeof thresholds === 'string' || Object.keys(thresholds).length > 0);
    if (!hasThresholds) {
      set((state) => ({ evaluation: { ...state.evaluation, canSubmit: false } }));
      return;
    }
    const meetsLocal = allDimensionsMeetThresholds(evaluation.data || {}, thresholds);
    const backendReadiness = evaluation.readiness ?? formativeFeedback.data?.isReadyToProgress ?? null;
    if (typeof backendReadiness === 'boolean') {
      set((state) => ({
        evaluation: { ...state.evaluation, canSubmit: backendReadiness && meetsLocal },
      }));
      return;
    }
    set((state) => ({ evaluation: { ...state.evaluation, canSubmit: meetsLocal } }));
  },

  initializeFromTask: (task) => {
    const initialHtml: string = task?.submission?.text || '';
    const initialWordcount = {
      bodyWords: countWords(initialHtml, true),
      allWords: countWords(initialHtml, false),
    };
    const bounds = wordCountBounds(task?.evaluation?.formalCriteria);
    const targets = bounds ?? { min: 0, target: 0, max: 0 };

    // Clean ETag (strip quotes from Cosmos DB format)
    const rawEtag = task?.submission?.etag || null;
    const cleanedEtag = rawEtag ? rawEtag.replace(/^["']|["']$/g, '') : null;

    // Preserve ETag if reinitializing the SAME task (e.g., after offline recovery)
    // This prevents losing our in-memory ETag when the cache might be stale
    const currentState = get();
    const isSameTask = currentState.submission.taskId === task?.id;
    const currentEtag = currentState.submission.etag;
    const finalEtag = (isSameTask && currentEtag) ? currentEtag : cleanedEtag;

    set((state) => ({
      submission: {
        ...state.submission,
        html: initialHtml,
        taskId: task?.id || null,
        wordcount: initialWordcount,
        wordcountTargets: targets,
        dirty: false,
        etag: finalEtag,
      },
      evaluation: {
        ...state.evaluation,
        data: (getFlatEvaluationDimensions(task?.submission?.evaluation) as SubmissionEvaluationResult) || null,
        error: null,
        completionPolicy: task?.evaluation?.completionPolicyEffective || 'free',
        minLevelsEffective: task?.evaluation?.completionMinLevelsEffective || null,
        readiness: task?.submission?.evaluationFormativeFeedback?.isReadyToProgress ?? null,
      },
      formativeFeedback: {
        data: task?.submission?.evaluationFormativeFeedback || null,
        partial: null,
        version: task?.submission?.evaluationFormativeFeedback ? 1 : 0,
        runId: null,
      },
    }));
    get().recomputeCanSubmit();
  },

  setAttachments: (items) => set((state) => ({ attachments: { ...state.attachments, items } })),
  setUploading: (flag) => set((state) => ({ attachments: { ...state.attachments, isUploading: flag } })),
  setAttachmentError: (message) => set((state) => ({ attachments: { ...state.attachments, lastError: message } })),

  setReadonly: (flag) => set((state) => ({ ui: { ...state.ui, readonly: flag } })),
  setOffline: (flag) => set((state) => ({ ui: { ...state.ui, isOffline: flag } })),

  setReacting: (flag) => set((state) => ({ reaction: { ...state.reaction, isReacting: flag } })),
  updatePendingReaction: (content) => {
    const current = get().reaction.pendingReactionMessage;
    const id = current?.id || `reaction-${Date.now()}`;
    set((state) => ({
      reaction: {
        ...state.reaction,
        pendingReactionMessage: { id, content, createdAt: current?.createdAt || new Date() },
      },
    }));
  },
  clearPendingReaction: () => set((state) => ({ reaction: { ...state.reaction, pendingReactionMessage: null, error: undefined } })),
  setReactionError: (message) => set((state) => ({ reaction: { ...state.reaction, error: message } })),

  setChatMessages: (messages) => set((state) => ({ chat: { ...state.chat, messages } })),
  appendChatMessage: (message) => set((state) => ({ chat: { ...state.chat, messages: [...state.chat.messages, message] } })),
  updateChatMessage: (id, updates) => set((state) => ({
    chat: {
      ...state.chat,
      messages: state.chat.messages.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg)),
    },
  })),
  clearChatMessages: () => set((state) => ({ chat: { ...state.chat, messages: [] } })),
  setFormativeFeedback: (data, runId) => set((state) => ({
    formativeFeedback: {
      ...state.formativeFeedback,
      data,
      partial: null,
      runId: data ? (runId ?? state.formativeFeedback.runId) : null,
      version: data ? state.formativeFeedback.version + 1 : state.formativeFeedback.version,
    },
  })),
  setFormativeFeedbackPartial: (partial) => set((state) => ({
    formativeFeedback: { ...state.formativeFeedback, partial },
  })),

  reset: (initial) => {
    set((state) => ({
      // Preserve taskId and etag across resets - they're needed for offline recovery
      // initializeFromTask will update them if we're switching tasks
      submission: {
        ...initialState.submission,
        taskId: state.submission.taskId,
        etag: state.submission.etag,
        ...(initial?.submission || {}),
      },
      autosave: { ...initialState.autosave, ...(initial?.autosave || {}) },
      evaluation: { ...initialState.evaluation, ...(initial?.evaluation || {}) },
      attachments: { ...initialState.attachments, ...(initial?.attachments || {}) },
      ui: { ...initialState.ui, ...(initial?.ui || {}) },
      reaction: { ...initialState.reaction, ...(initial?.reaction || {}) },
      chat: { ...initialState.chat, ...(initial?.chat || {}) },
      formativeFeedback: { ...initialState.formativeFeedback, ...(initial?.formativeFeedback || {}) },
    }));
  },
}));

export default useTaskSubmissionStore;

export const taskSubmissionSelectors = {
  content: (s: TaskSubmissionState) => s.submission.html,
  wordcount: (s: TaskSubmissionState) => s.submission.wordcount,
  dirty: (s: TaskSubmissionState) => s.submission.dirty,
  taskId: (s: TaskSubmissionState) => s.submission.taskId,
  etag: (s: TaskSubmissionState) => s.submission.etag,
  autosave: (s: TaskSubmissionState) => s.autosave,
  evaluation: (s: TaskSubmissionState) => s.evaluation,
  canSubmit: (s: TaskSubmissionState) => s.evaluation.canSubmit,
  chatMessages: (s: TaskSubmissionState) => s.chat.messages,
  formativeFeedback: (s: TaskSubmissionState) => s.formativeFeedback.data,
};

export const useTaskSubmissionActions = () => useTaskSubmissionStore(
  (s) => ({
    setContent: s.setContent,
    setContentAndWordcount: s.setContentAndWordcount,
    setWordcount: s.setWordcount,
    setDirty: s.setDirty,
    setAutosaveState: s.setAutosaveState,
    setLastSavedAt: s.setLastSavedAt,
    setEtag: s.setEtag,
    setEvaluation: s.setEvaluation,
    setEvaluationDim: s.setEvaluationDim,
    setExpectedDims: s.setExpectedDims,
    setEvaluationProgress: s.setEvaluationProgress,
    clearEvaluationStreamUi: s.clearEvaluationStreamUi,
    clearEvaluationError: s.clearEvaluationError,
    failEvaluation: s.failEvaluation,
    abortEvaluation: s.abortEvaluation,
    resetEvaluation: s.resetEvaluation,
    setEvaluating: s.setEvaluating,
    setCompletionPolicy: s.setCompletionPolicy,
    setMinLevelsEffective: s.setMinLevelsEffective,
    setReadiness: s.setReadiness,
    recomputeCanSubmit: s.recomputeCanSubmit,
    initializeFromTask: s.initializeFromTask,
    setAttachments: s.setAttachments,
    setUploading: s.setUploading,
    setAttachmentError: s.setAttachmentError,
    setReadonly: s.setReadonly,
    setOffline: s.setOffline,
    setReacting: s.setReacting,
    updatePendingReaction: s.updatePendingReaction,
    clearPendingReaction: s.clearPendingReaction,
    setReactionError: s.setReactionError,
    setChatMessages: s.setChatMessages,
    appendChatMessage: s.appendChatMessage,
    updateChatMessage: s.updateChatMessage,
    clearChatMessages: s.clearChatMessages,
    setFormativeFeedback: s.setFormativeFeedback,
    setFormativeFeedbackPartial: s.setFormativeFeedbackPartial,
    reset: s.reset,
  }),
  shallow,
);

export const useTaskSubmissionState = () => useTaskSubmissionStore(
  (s) => ({
    autosave: s.autosave,
    canSubmit: s.evaluation.canSubmit,
    isEvaluating: s.evaluation.isEvaluating,
    isUploading: s.attachments.isUploading,
    dirty: s.submission.dirty,
    lastSavedAt: s.submission.lastSavedAt,
    etag: s.submission.etag,
    wordcount: s.submission.wordcount,
    content: s.submission.html,
    wordcountTargets: s.submission.wordcountTargets,
    wordcountColor: getWordcountColor(
      s.submission.wordcount.bodyWords,
      s.submission.wordcountTargets.min,
      s.submission.wordcountTargets.target,
      s.submission.wordcountTargets.max,
    ),
    evaluation: s.evaluation.data,
    evaluatedAt: s.evaluation.evaluatedAt,
    minLevelsEffective: s.evaluation.minLevelsEffective,
    expectedDims: s.evaluation.expectedDims,
    evaluationProgress: s.evaluation.progress,
    evaluationError: s.evaluation.error,
    formativeFeedback: s.formativeFeedback.data,
    formativeFeedbackPartial: s.formativeFeedback.partial,
    formativeFeedbackVersion: s.formativeFeedback.version,
    formativeFeedbackRunId: s.formativeFeedback.runId,
    isOffline: s.ui.isOffline,
  }),
  shallow,
);
